import type {
  Simulation,
  SimulationShipmentOptions,
  SimulationTntOptions,
  SimulationUpload,
} from '~/store/simulation/types';
import { SimulationContextLinkValidationMessage, CarrierListMode } from '~/store/simulation/types';

import type { CarrierSelectionSummary } from '~/store/services/DatalakeService/types';
import { format, isAfter, isBefore } from 'date-fns';
import { TNT_OVERRIDE_DEFAULT_OPTIONS } from './shared/TntOverrideStep';
import type {
  UIOrchestrationCarrierSummary,
  PartnerCarrierRateSet,
  PartnerContract,
} from '@shipium-corp/shipium-ts-types';
import type { ContextTableSummary } from '~/store/fulfillmentContext/helpers';
import type { PartnerCarrierRateSummary } from '~/services/partner-management-service';

export interface OriginContextRecord extends OriginPostalRecord {
  fulfillmentContextName: string;
  fulfillmentContextId: string;
}

export interface OriginPostalRecord {
  minShipDate: string;
  maxShipDate: string;
  shipmentCount: number;
  originPostalCode: string;
}

export const isContextRecord = (record: OriginPostalRecord | OriginContextRecord): record is OriginContextRecord => {
  return (record as OriginContextRecord).fulfillmentContextName !== undefined;
};

const VALID_SAVED_PRODUCT_DETAILS: (keyof SimulationShipmentOptions)[] = ['hazmat', 'lithiumIon', 'limitedQuantity'];

export const generateOriginRecordsFromUpload = (simulationUploads: SimulationUpload[]): OriginPostalRecord[] => {
  const originMap: Record<string, OriginPostalRecord> = {};
  for (const val of simulationUploads as any[]) {
    for (const o of Object.values(val?.details?.origins ?? {}) as any[]) {
      const entry = originMap[o.originPostalCode];
      const newMin = !entry || entry.minShipDate > o.minShipDate ? o.minShipDate : entry.minShipDate;
      const newMax = !entry || entry.maxShipDate < o.maxShipDate ? o.maxShipDate : entry.maxShipDate;
      const newCount = (entry?.shipmentCount ?? 0) + Number(o.shipmentCount);
      originMap[o.originPostalCode] = {
        originPostalCode: o.originPostalCode,
        minShipDate: newMin,
        maxShipDate: newMax,
        shipmentCount: newCount,
      };
    }
  }
  return Object.values(originMap);
};
export const generateOriginRecordsFromCarrierSelectionSummaries = (
  summaries: CarrierSelectionSummary[],
): OriginPostalRecord[] => {
  const originMap: Record<string, OriginPostalRecord> = {};
  for (const curr of summaries) {
    const existing = originMap[curr.fromPostal];
    if (existing) {
      originMap[curr.fromPostal] = {
        originPostalCode: curr.fromPostal,
        minShipDate: curr.shippedDate < existing.minShipDate ? curr.shippedDate : existing.minShipDate,
        maxShipDate: curr.shippedDate > existing.maxShipDate ? curr.shippedDate : existing.maxShipDate,
        shipmentCount: Number(curr.carrierSelectionCount) + existing.shipmentCount,
      };
    } else {
      originMap[curr.fromPostal] = {
        originPostalCode: curr.fromPostal,
        minShipDate: curr.shippedDate,
        maxShipDate: curr.shippedDate,
        shipmentCount: Number(curr.carrierSelectionCount),
      };
    }
  }
  return Object.values(originMap);
};
export const generateContextRecordsFromCarrierSelectionSummaries = (
  summaries: CarrierSelectionSummary[],
  contextNameMap: Record<string, string>,
): OriginContextRecord[] => {
  const contextMap: Record<string, OriginContextRecord> = {};
  for (const curr of summaries) {
    const existing = contextMap[curr.fulfillmentContextId];
    if (existing) {
      contextMap[curr.fulfillmentContextId] = {
        fulfillmentContextId: curr.fulfillmentContextId,
        fulfillmentContextName: contextNameMap[curr.fulfillmentContextId] || curr.fulfillmentContextId,
        // First-seen wins, matching generateContextRecordsFromUpload.
        originPostalCode: existing.originPostalCode,
        minShipDate: curr.shippedDate < existing.minShipDate ? curr.shippedDate : existing.minShipDate,
        maxShipDate: curr.shippedDate > existing.maxShipDate ? curr.shippedDate : existing.maxShipDate,
        shipmentCount: Number(curr.carrierSelectionCount) + existing.shipmentCount,
      };
    } else {
      contextMap[curr.fulfillmentContextId] = {
        fulfillmentContextId: curr.fulfillmentContextId,
        fulfillmentContextName: contextNameMap[curr.fulfillmentContextId] || curr.fulfillmentContextId,
        originPostalCode: curr.fromPostal,
        minShipDate: curr.shippedDate,
        maxShipDate: curr.shippedDate,
        shipmentCount: Number(curr.carrierSelectionCount),
      };
    }
  }
  return Object.values(contextMap);
};

export const generateContextRecordsFromUpload = (
  simulationUploads: SimulationUpload[],
  contextNameMap: Record<string, string>,
  fulfillmentContextTableSummaries: ContextTableSummary[],
): OriginContextRecord[] => {
  const hasFulfillmentContexts = simulationUploads.some((upload) => upload.details?.fulfillmentContexts);
  if (!hasFulfillmentContexts) return [];

  // Index summaries by context id once. `in` (not truthiness) preserves the
  // first-match-wins semantics the previous per-row `.find()` had.
  const postalByContextId: Record<string, string> = {};
  for (const summary of fulfillmentContextTableSummaries ?? []) {
    const id = summary.context.fulfillmentContextId;
    if (!(id in postalByContextId)) {
      postalByContextId[id] = summary.address?.location?.postalCode || '';
    }
  }

  const contextMap: Record<string, OriginContextRecord> = {};
  for (const upload of simulationUploads) {
    const fulfillmentContexts = upload.details?.fulfillmentContexts || {};
    for (const [fulfillmentContextId, contextData] of Object.entries(fulfillmentContexts)) {
      const originPostalCode = postalByContextId[fulfillmentContextId] || '';

      const existing = contextMap[fulfillmentContextId];
      if (existing) {
        contextMap[fulfillmentContextId] = {
          fulfillmentContextId,
          fulfillmentContextName:
            contextNameMap[fulfillmentContextId] || contextData.fulfillmentContextName || fulfillmentContextId,
          originPostalCode,
          minShipDate: contextData.minShipDate < existing.minShipDate ? contextData.minShipDate : existing.minShipDate,
          maxShipDate: contextData.maxShipDate > existing.maxShipDate ? contextData.maxShipDate : existing.maxShipDate,
          shipmentCount: contextData.shipmentCount + existing.shipmentCount,
        };
      } else {
        contextMap[fulfillmentContextId] = {
          fulfillmentContextId,
          fulfillmentContextName:
            contextNameMap[fulfillmentContextId] || contextData.fulfillmentContextName || fulfillmentContextId,
          originPostalCode,
          minShipDate: contextData.minShipDate,
          maxShipDate: contextData.maxShipDate,
          shipmentCount: contextData.shipmentCount,
        };
      }
    }
  }
  return Object.values(contextMap);
};

export const shapeShipOptions = (data: any) => {
  const {
    timeInTransitDays,
    productDetails,
    destinationIsPOBox,
    shipOption,
    desiredDeliveryDate,
    businessDaysOfTransit,
    destinationAddressType,
    ignoreRuleSets,
    shiptimeVersion,
    costAdjustLimitEnabled,
    costAdjustLimitTargetCarrier,
    costAdjustLimitExcludedCarriers,
    targetPercent,
    seedAdjustment,
    costAdjustTargetBehavior,
    ignoreCarrierServiceMethodAllowList,
    carrierServiceMethodAllowList,
    carrierServiceMethodExclusionList,
  } = data;

  return {
    shiptimeVersion: shiptimeVersion || '2',
    ...(timeInTransitDays ? { timeInTransitDays } : {}),
    limitedQuantity: productDetails?.includes('limitedQuantity'),
    lithiumIon: productDetails?.includes('lithiumIon'),
    hazmat: productDetails?.includes('hazmat'),
    ignoreRuleSets: ignoreRuleSets,
    ...(carrierServiceMethodAllowList?.length
      ? {
          carrierServiceMethodAllowList,
        }
      : {}),
    ...(carrierServiceMethodExclusionList?.length
      ? {
          carrierServiceMethodExclusionList,
        }
      : {}),
    ignoreCarrierServiceMethodAllowList: ignoreCarrierServiceMethodAllowList,
    ...(destinationIsPOBox || destinationIsPOBox === false ? { destinationIsPOBox: !!destinationIsPOBox } : {}),
    shipOption,
    ...(desiredDeliveryDate
      ? {
          desiredDeliveryDate: format(desiredDeliveryDate, 'yyyy-MM-dd'),
        }
      : {}),
    businessDaysOfTransit,
    destinationAddressType,
    costAdjustLimitEnabled,
    costAdjustLimitTargetCarrier,
    costAdjustLimitExcludedCarriers,
    costAdjustLimitTargetPercent: targetPercent,
    costAdjustLimitSeedAdjustment: seedAdjustment,
    costAdjustLimitTargetBehavior: costAdjustTargetBehavior,
  };
};
export const shapeTntOptions = (data: any) => {
  const {
    additionalDaysToProcess,
    timeInTransitSetting,
    timeInTransitSourcePreference,
    weekendConsideredTransitDay,
    tntOverride,
    tntOverrideDataSetId,
    timeInTransitModelSource,
  } = data;
  return {
    ...(additionalDaysToProcess ? { additionalDaysToProcess: additionalDaysToProcess } : {}),
    ...(timeInTransitSourcePreference ? { timeInTransitSourcePreference: timeInTransitSourcePreference } : {}),
    ...(weekendConsideredTransitDay ? { weekendConsideredTransitDay: weekendConsideredTransitDay } : {}),
    ...(tntOverride ? { tntOverride } : {}),
    ...(tntOverrideDataSetId ? { tntOverrideDataSetId } : {}),
    ...(timeInTransitSetting && timeInTransitModelSource === 'shipment_delivery_prediction'
      ? { timeInTransitSetting }
      : {}),
    ...(timeInTransitModelSource ? { timeInTransitModelSource } : {}),
  };
};

export const flattenTntOptionsToFormData = (simulationTntOptions: SimulationTntOptions) => {
  const {
    timeInTransitSourcePreference = [],
    weekendConsideredTransitDay,
    additionalDaysToProcess,
    tntOverride,
    timeInTransitSetting = TNT_OVERRIDE_DEFAULT_OPTIONS.timeInTransitSetting,
    timeInTransitModelSource = TNT_OVERRIDE_DEFAULT_OPTIONS.timeInTransitModelSource,
  } = simulationTntOptions ?? {};

  return {
    timeInTransitSourcePreference,
    weekendConsideredTransitDay,
    additionalDaysToProcess,
    tntOverride,
    timeInTransitSetting,
    timeInTransitModelSource,
  };
};

export const flattenBaseDetailsToFormData = (data: Simulation) => {
  const {
    name,
    description,
    executionPriority,
    includesHistoricalSelection,
    simulationShipmentOptions,
    simulationCostingOptions,
  } = data;
  const {
    ignoreRuleSets,
    ignoreCarrierServiceMethodAllowList,
    carrierServiceMethodAllowList,
    carrierServiceMethodExclusionList,
    shipOption,
    destinationIsPOBox,
    destinationAddressType,
    desiredDeliveryDate,
    timeInTransitDays,
    businessDaysOfTransit,
    shiptimeVersion,
    costAdjustLimitEnabled,
    costAdjustLimitTargetCarrier,
    costAdjustLimitExcludedCarriers,
    costAdjustLimitTargetPercent,
    costAdjustLimitSeedAdjustment,
    costAdjustLimitTargetBehavior,
  } = simulationShipmentOptions ?? {};

  const carrierListMode = getCarrierListMode(carrierServiceMethodExclusionList);

  const { totalUpgradeCostTolerance, upgradeOverCheapestCostTolerance } = simulationCostingOptions ?? {};

  return {
    name,
    description,
    executionPriority,
    shiptimeVersion,
    includesHistoricalSelection,
    totalUpgradeCostTolerance,
    upgradeOverCheapestCostTolerance,
    productDetails: VALID_SAVED_PRODUCT_DETAILS.filter(
      (v: keyof SimulationShipmentOptions) => !!(simulationShipmentOptions as any)?.[v],
    ),
    ignoreRuleSets,
    ignoreCarrierServiceMethodAllowList,
    carrierServiceMethodList:
      carrierListMode === CarrierListMode.EXCLUDE && carrierServiceMethodExclusionList?.length
        ? carrierServiceMethodExclusionList
        : carrierServiceMethodAllowList,
    carrierListMode,
    shipOption,
    destinationIsPOBox,
    destinationAddressType,
    ...(desiredDeliveryDate ? { desiredDeliveryDate: new Date(desiredDeliveryDate) } : {}),
    timeInTransitDays,
    businessDaysOfTransit,
    costAdjustLimitEnabled,
    costAdjustLimitTargetCarrier,
    costAdjustLimitExcludedCarriers,
    targetPercent: costAdjustLimitTargetPercent,
    seedAdjustment: costAdjustLimitSeedAdjustment,
    costAdjustTargetBehavior: costAdjustLimitTargetBehavior,
  };
};

export const getCarrierListMode = (exclusionList?: string[]): CarrierListMode =>
  exclusionList?.length ? CarrierListMode.EXCLUDE : CarrierListMode.ALLOW;

const getConfiguredServiceMethodIds = (partnerCarrierSummaries?: UIOrchestrationCarrierSummary[]): string[] => {
  return partnerCarrierSummaries && Array.isArray(partnerCarrierSummaries)
    ? partnerCarrierSummaries.flatMap(
        (carrier) =>
          carrier.serviceMethods
            ?.filter((serviceMethod) => serviceMethod.configured)
            .map((serviceMethod) => serviceMethod.serviceMethodId) ?? ([] as string[]),
      )
    : [];
};

export const formatCarrierListsForStorage = (
  formCarrierServiceMethodList: string[] | undefined,
  formCarrierListMode: CarrierListMode,
  partnerCarrierSummaries?: UIOrchestrationCarrierSummary[],
): { carrierServiceMethodAllowList: string[]; carrierServiceMethodExclusionList: string[] } => {
  const configuredServiceMethodIds = getConfiguredServiceMethodIds(partnerCarrierSummaries);
  const isExclusionList = formCarrierListMode === CarrierListMode.EXCLUDE && formCarrierServiceMethodList?.length;

  return isExclusionList
    ? {
        // get subset of configured service method ids that are not in the exclusion list
        carrierServiceMethodAllowList: configuredServiceMethodIds.filter(
          (id) => !formCarrierServiceMethodList?.includes(id),
        ),
        carrierServiceMethodExclusionList: formCarrierServiceMethodList ?? [],
      }
    : { carrierServiceMethodAllowList: formCarrierServiceMethodList ?? [], carrierServiceMethodExclusionList: [] };
};

const checkOverlap = (startDate: string, endDate: string, minShipDate: string, maxShipDate: string): boolean => {
  const minDate = new Date(minShipDate),
    maxDate = new Date(maxShipDate),
    entryFrom = new Date(startDate),
    entryTo = new Date(endDate);
  return (
    (isBefore(entryFrom, maxDate) || entryFrom.getTime() === maxDate.getTime()) &&
    (isAfter(entryTo, minDate) || entryTo.getTime() === minDate.getTime())
  );
};

export const checkScheduleOverlap = (
  scheduleEntries: { from: string; to: string }[],
  minShipDate: string,
  maxShipDate: string,
): boolean => {
  if (!scheduleEntries?.length) return false;
  return scheduleEntries.some((entry) => checkOverlap(entry.from, entry.to, minShipDate, maxShipDate));
};

export const checkCarrierRateOverlap = (
  contracts: PartnerContract[],
  carrierRateSetsMap: Record<string, PartnerCarrierRateSet>,
  carrierRatesMap: Record<string, PartnerCarrierRateSummary>,
  minShipDate: string,
  maxShipDate: string,
): boolean => {
  if (!contracts?.length) return false;
  for (const contract of contracts) {
    const rateSet = carrierRateSetsMap[contract?.carrierRateSetId ?? ''];
    if (!rateSet?.carrierRateIds) continue;
    for (const rateId of rateSet.carrierRateIds) {
      const rate = carrierRatesMap[rateId];
      const rateCalendar = rate?.rateCalendar;
      if (!rate || !rateCalendar) continue;
      for (const entry of rateCalendar) {
        if (checkOverlap(entry.startDateTime, entry.endDateTime, minShipDate, maxShipDate)) {
          return true;
        }
      }
    }
  }
  return false;
};

export const validateContextLink = ({
  contextSummary,
  contractsMap,
  carrierRateSetsMap,
  carrierRatesMap,
  originPostalRecord,
}: {
  contextSummary: ContextTableSummary;
  contractsMap: Record<string, PartnerContract>;
  carrierRateSetsMap: Record<string, PartnerCarrierRateSet>;
  carrierRatesMap: Record<string, PartnerCarrierRateSummary>;
  originPostalRecord: OriginPostalRecord | OriginContextRecord;
}): string[] => {
  const messages: string[] = [];

  if (!contextSummary?.schedule) {
    messages.push(SimulationContextLinkValidationMessage.NO_SCHEDULE);
  } else if (
    !checkScheduleOverlap(
      contextSummary.schedule.entries,
      originPostalRecord.minShipDate,
      originPostalRecord.maxShipDate,
    )
  ) {
    messages.push(SimulationContextLinkValidationMessage.NO_SCHEDULE_OVERLAP);
  }

  const contractIds = contextSummary?.context?.contractIds || [];
  const injectionProfileIds = contextSummary?.context?.injectionProfileIds ?? [];
  if (injectionProfileIds.length && injectionProfileIds.length > 0) {
    return messages;
  }
  const contextContracts = contractIds.map((id: string) => contractsMap[id]).filter(Boolean);
  if (!contextContracts.length) {
    messages.push(SimulationContextLinkValidationMessage.NO_CARRIER_RATES);
  } else if (
    !checkCarrierRateOverlap(
      contextContracts,
      carrierRateSetsMap,
      carrierRatesMap,
      originPostalRecord.minShipDate,
      originPostalRecord.maxShipDate,
    )
  ) {
    messages.push(SimulationContextLinkValidationMessage.NO_CARRIER_RATES_OVERLAP);
  }

  return messages;
};
